import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import { view } from "@risingstack/react-easy-state";
import isNil from "lodash-es/isNil";
import type React from "react";
import { useEffect, useState } from "react";

import { logger } from "../../common/logger";
import { sendFilter } from "../../common/tracking";
import type { FilterSettings, LegalFilterKey, Product } from "../../common/types/productTypes";
import useMediaQuery from "../../common/useMediaQuery";
import { twDesktopBreakpoint } from "../../common/utils";
import featuresStore from "../../stores/features/featuresStore";
import productStore from "../../stores/product/productStore";
import { buildFilter, getRelevantFilterSettings } from "../../stores/product/productUtils";
import theme from "../../themes/theme";
import Button from "../Button";
import Modal from "../Modal";
import FilterOptions from "./FilterOptions";

const filterAttributesFunction = theme.filterAttributes;

type FilterPanelProps = {
   products?: Product[];
   skus?: string[];
   variant?: string;
   numberOfProducts?: number;
};

const FilterPanel = ({ products, skus = [], variant = "", numberOfProducts }: FilterPanelProps) => {
   const [mobileFilterVisible, setMobileFilterVisible] = useState(false);
   const isDesktop = useMediaQuery(twDesktopBreakpoint);
   const resolvedProducts = !isNil(products) ? products : productStore.resolveSkus(skus);

   const filterAttributes = filterAttributesFunction(variant);
   logger.log("search", "Filter attributes", filterAttributes);

   if (featuresStore.navigationTestVariant === "megamenu") {
      filterAttributes.brandGroup = "Merkevare";

      if (/ost/g.test(variant)) {
         filterAttributes.format = "Format";
      }
   }
   const allFilters: FilterSettings[] = [];
   const filterKeys = Object.keys(filterAttributes) as LegalFilterKey[];
   for (let i = 0; i < filterKeys.length; i++) {
      const fieldKey = filterKeys[i];
      const fieldName = filterAttributes[fieldKey];

      if (isNil(fieldName)) {
         continue;
      }

      const filter = buildFilter(resolvedProducts, fieldKey, fieldName);
      if (!isNil(filter)) {
         allFilters.push(filter);
      }
   }
   logger.log("search", "All filters", allFilters);

   const relevantFilters = getRelevantFilterSettings(allFilters, productStore.activeFilters, resolvedProducts);

   useEffect(() => {
      for (const filter of relevantFilters) {
         const fieldKey = filter.field;
         // a radio button filter must have a value.  If there is no active filter, we set it here
         if (filter.radioDefault && !productStore.activeFilters[fieldKey]?.length) {
            productStore.addFilterValue(fieldKey, filter.radioDefault);
         }
      }

      const { activeFilters } = productStore;
      if (activeFilters) {
         const filtersToReset = Object.keys(activeFilters).filter((key) => {
            const activeFilter = activeFilters[key as LegalFilterKey];
            if (
               activeFilter &&
               activeFilter.length > 0 &&
               !relevantFilters.find((relevantFilter) => relevantFilter.field === key)
            ) {
               return true;
            }
            return false;
         });
         productStore.clearFilterFields(filtersToReset as LegalFilterKey[]);
      }
   }, [JSON.stringify(relevantFilters)]);

   logger.log("search", "Relevant filters", relevantFilters);

   const filterModified = (e: React.ChangeEvent<HTMLInputElement>, field: LegalFilterKey, value: string, isRadio: boolean) => {
      if (e.target.checked) {
         if (isRadio) {
            productStore.clearFilterFields([field]);
         }
         productStore.addFilterValue(field, value);
         sendFilter(field, value);
      } else {
         if (isRadio) {
            const currentFilterValue = productStore.activeFilters[field]?.find((af) => af && af !== value);
            if (currentFilterValue) {
               productStore.removeFilterValue(field, currentFilterValue);
            }
            productStore.addFilterValue(field, value);
            sendFilter(field, value);
         } else {
            productStore.removeFilterValue(field, value);
         }
      }
   };

   return (
      <>
         {/* Mobile version */}
         {!isDesktop && (
            <>
               <Button
                  icon={<AdjustmentsHorizontalIcon />}
                  iconPosition="left"
                  className="tw-mb-4"
                  onClick={() => setMobileFilterVisible(!mobileFilterVisible)}
               >
                  Filter
               </Button>
               <Modal
                  title="Filter"
                  isOpen={mobileFilterVisible}
                  onClose={() => setMobileFilterVisible(false)}
                  name="Mobilfilter"
               >
                  {/* Scrollable content */}
                  <div className="tw-relative tw-max-h-[65vh] tw-overflow-y-auto">
                     <FilterOptions filters={relevantFilters} onFilterChange={filterModified} />
                     <div className="tw-sticky tw-bottom-0 tw-left-0 tw-w-full tw-h-12 tw-bg-gradient-to-t tw-from-surface-default tw-pointer-events-none" />
                  </div>

                  {/* Sticky button container */}
                  <div className="tw-sticky tw-bottom-0 tw-left-0 tw-mt-4">
                     <Button onClick={() => setMobileFilterVisible(false)}>Vis {numberOfProducts} produkter</Button>
                  </div>
               </Modal>
            </>
         )}

         {/* Desktop version */}
         {isDesktop && <FilterOptions filters={relevantFilters} onFilterChange={filterModified} />}
      </>
   );
};

export default view(FilterPanel);
